import React, { useState, createContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { normalizeEmailAddress, isSmtpAddress } from '../shared/utils/office';

// A ReadPanel specific context that stores Office.context.mailbox.item data via the Office.MessageRead interface.
// https://docs.microsoft.com/en-us/javascript/api/outlook/office.messageread?view=outlook-js-preview
const MessageReadItemContext = createContext();

// Determines whether the message was received by (inbound) rather than sent by
// the signed-in user. Office.js sometimes returns the sender as an Exchange
// X500 / legacyExchangeDN address instead of SMTP for internal senders; a naive
// string compare then treats the user's own outbound mail as inbound, which
// inverted the sender/recipient in logged outreaches (THRIVE-18862). We compare
// case-insensitively, and when the sender is not a real SMTP address we fall
// back to display-name comparison so internal senders resolve correctly.
const deriveIsInbound = (from, userProfile) => {
  const userEmail = normalizeEmailAddress(userProfile?.emailAddress);
  const fromEmail = normalizeEmailAddress(from?.emailAddress);

  if (isSmtpAddress(fromEmail)) {
    return fromEmail !== userEmail;
  }

  const userName = (userProfile?.displayName || '').trim().toLowerCase();
  const fromName = (from?.displayName || '').trim().toLowerCase();

  // With a usable display name on both sides, compare those.
  if (userName && fromName) {
    return fromName !== userName;
  }
  // Non-SMTP sender we cannot disambiguate by name: a non-SMTP (X500/Exchange)
  // `from` is almost always the signed-in user's own internal mail, so bias
  // toward outbound rather than re-introducing the inversion bug.
  return false;
};

const MessageReadItemContextProvider = ({ children }) => {
  const [messageContext, setMessageContext] = useState(null);
  // Set when body.getAsync fails, so we can show a retry path instead of an
  // indefinite loader when no further ItemChanged event follows the failure.
  const [loadError, setLoadError] = useState(false);
  // Bumped by the Outlook ItemChanged event (and the retry button) so the
  // context re-resolves against the currently-selected message.
  const [refreshKey, setRefreshKey] = useState(0);

  const { Office } = window;

  useEffect(() => {
    const { mailbox } = Office.context;
    // Not every host/mock supports ItemChanged (e.g. the Cypress mailbox mock is a plain object
    // with no addHandlerAsync). Guard so the panel still mounts; it just won't auto-refresh there.
    if (typeof mailbox.addHandlerAsync !== 'function') {
      return () => {};
    }

    const handler = () => setRefreshKey((key) => key + 1);
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
    return () => {
      if (typeof mailbox.removeHandlerAsync === 'function') {
        // Mailbox.removeHandlerAsync has no overload that removes one specific
        // handler - its second argument is options or a completion callback, so
        // passing `handler` there would invoke it (bumping refreshKey during
        // cleanup) rather than deregistering it. Removing every ItemChanged
        // handler is correct here: this effect registers the only one.
        mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
      }
    };
  }, [Office.context, Office.EventType?.ItemChanged]);

  /* The Office.js API model uses a local Javascript proxy object as placeholder for the body property value.
  ```js
  Office.mailbox.item.body => {
    getAsync: function(type, body => ... ) {...}
  }
  ```
  As the ReadPanel `item` represents a previously sent message with static values,
  all the used values are resolved once when the panel opens.

  - More on Office.js proxy objects: https://buildingofficeaddins.com/proxy-objects/
  - More on JS proxy objects: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy
  */
  useEffect(() => {
    // Guards against the ItemChanged race: if a newer event supersedes this run, its cleanup flips
    // `cancelled` so the in-flight getAsync callback below is ignored and cannot overwrite the newer
    // message's context.
    let cancelled = false;

    // Re-read the item fresh each run so an ItemChanged event picks up the
    // currently-selected message rather than a captured proxy.
    const { item, userProfile } = Office.context.mailbox;

    // Outlook can clear the selection (item === null) while switching messages. Show the loader and
    // wait for the next ItemChanged instead of dereferencing a null item.
    if (!item) {
      setMessageContext(null);
      return () => {};
    }

    // Clear immediately so the panel cannot log the previous message's data while the new body loads,
    // and reset any prior error so this fresh attempt (or retry) starts clean.
    setMessageContext(null);
    setLoadError(false);

    const isInboundMessage = deriveIsInbound(item.from, userProfile);

    // Only forward a sender address the server can trust (real SMTP). For
    // X500/Exchange forms we omit it so the server falls back to the
    // client-derived `isInbound` above instead of failing an SMTP match.
    const senderEmail = isSmtpAddress(item.from?.emailAddress)
      ? item.from.emailAddress
      : null;

    const userEmail = normalizeEmailAddress(userProfile?.emailAddress);
    const contactEmails = [item.from, ...(item.to || [])]
      .filter(Boolean)
      .map((recipient) => recipient.emailAddress)
      .filter(
        (emailAddress) => normalizeEmailAddress(emailAddress) !== userEmail,
      );

    item.body.getAsync(Office.CoercionType.Html, (asyncResult) => {
      // Ignore a callback superseded by a newer ItemChanged, and bail on Outlook async failures
      // (including item-changed errors) rather than writing partial/stale context.
      if (cancelled) {
        return;
      }
      // Bail only on an explicit failure. (Not `!== Succeeded`: the Cypress mailbox mock invokes the
      // callback without a status, and treating that as failure would break the existing flow.)
      if (asyncResult.status === Office.AsyncResultStatus?.Failed) {
        setMessageContext(null);
        setLoadError(true);
        return;
      }

      setMessageContext({
        message: asyncResult.value,
        subject: item.normalizedSubject,
        internetMessageId: item.internetMessageId,
        conversationId: item.conversationId,
        isInbound: Boolean(isInboundMessage),
        senderEmail: senderEmail,
        contactedOn: item?.dateTimeCreated?.toString(),
        contactEmails: contactEmails,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    Office.CoercionType.Html,
    Office.AsyncResultStatus,
    Office.context.mailbox,
    refreshKey,
  ]);

  if (loadError) {
    return (
      <div className='u-textAlign-c u-marginTop-32 u-padding-24'>
        <p className='u-marginBottom-16'>
          We couldn&apos;t load this message. Please try again.
        </p>
        <button onClick={() => setRefreshKey((key) => key + 1)} type='button'>
          Retry
        </button>
      </div>
    );
  }

  return messageContext ? (
    <MessageReadItemContext.Provider value={messageContext}>
      {children}
    </MessageReadItemContext.Provider>
  ) : (
    <div className='loader' />
  );
};

MessageReadItemContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MessageReadItemContext;

export { MessageReadItemContextProvider, deriveIsInbound };
